/** Player collision settings for library / custom block definitions. */

export const BLOCK_COLLISION_GROUPS = [
  { id: 0, label: 'Default' },
  { id: 1, label: 'Decoration' },
  { id: 2, label: 'Foliage' },
  { id: 3, label: 'Mechanisms' },
] as const;

export type BlockCollisionGroupId = (typeof BLOCK_COLLISION_GROUPS)[number]['id'];

export interface BlockCollisionParams {
  /** When false, the player walks through the cell. Build raycast stays full-cell. */
  collideWithPlayer: boolean;
  /** Logical group for filters / future entity rules. */
  group: BlockCollisionGroupId;
  /**
   * Custom blocks only: player collider is the axis-aligned box around occupied
   * micro-voxels (still placed on the 1 m world grid).
   */
  shrinkHitbox: boolean;
}

export const DEFAULT_BLOCK_COLLISION: BlockCollisionParams = {
  collideWithPlayer: true,
  group: 0,
  shrinkHitbox: false,
};

/** Axis-aligned box in cell-local 0..1 space. */
export interface CellHitbox {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

export const FULL_CELL_HITBOX: CellHitbox = {
  minX: 0,
  minY: 0,
  minZ: 0,
  maxX: 1,
  maxY: 1,
  maxZ: 1,
};

export function isValidCollisionGroup(value: unknown): value is BlockCollisionGroupId {
  return typeof value === 'number'
    && Number.isInteger(value)
    && BLOCK_COLLISION_GROUPS.some((entry) => entry.id === value);
}

/** Normalize API / DB JSON into params (defaults when missing / invalid). */
export function parseBlockCollision(raw: unknown): BlockCollisionParams {
  if (raw == null || raw === '') return { ...DEFAULT_BLOCK_COLLISION };
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return { ...DEFAULT_BLOCK_COLLISION };
    }
  }
  if (!value || typeof value !== 'object') return { ...DEFAULT_BLOCK_COLLISION };
  const record = value as Record<string, unknown>;
  return {
    collideWithPlayer: record.collideWithPlayer !== false,
    group: isValidCollisionGroup(record.group) ? record.group : 0,
    shrinkHitbox: record.shrinkHitbox === true,
  };
}

/** Reject invalid bodies; undefined means “leave unchanged” for PATCH. */
export function resolveCollisionBody(
  raw: unknown,
): BlockCollisionParams | undefined | false {
  if (raw === undefined) return undefined;
  if (raw === null) return { ...DEFAULT_BLOCK_COLLISION };
  if (!raw || typeof raw !== 'object') return false;
  const record = raw as Record<string, unknown>;
  if (
    record.collideWithPlayer !== undefined
    && typeof record.collideWithPlayer !== 'boolean'
  ) {
    return false;
  }
  if (record.group !== undefined && !isValidCollisionGroup(record.group)) {
    return false;
  }
  if (
    record.shrinkHitbox !== undefined
    && typeof record.shrinkHitbox !== 'boolean'
  ) {
    return false;
  }
  return parseBlockCollision(raw);
}

/** Library cubes are always full-cell; shrink only applies to custom geometry. */
export function forLibraryCollision(collision: BlockCollisionParams): BlockCollisionParams {
  return { ...collision, shrinkHitbox: false };
}

export function collisionJson(collision: BlockCollisionParams): string {
  return JSON.stringify(collision);
}

/**
 * Tight AABB in cell-local 0..1 space from sparse micro-voxels.
 * Index layout matches optimizeCustomBlockVoxels: x + z*S + y*S*S.
 */
export function tightHitboxFromVoxels(
  voxels: ReadonlyArray<readonly [number, unknown]>,
  gridSize: number,
): CellHitbox | null {
  if (voxels.length === 0 || gridSize < 1) return null;
  let minVX = gridSize;
  let minVY = gridSize;
  let minVZ = gridSize;
  let maxVX = -1;
  let maxVY = -1;
  let maxVZ = -1;
  for (const [index] of voxels) {
    if (!Number.isInteger(index) || index < 0) continue;
    const x = index % gridSize;
    const z = Math.floor(index / gridSize) % gridSize;
    const y = Math.floor(index / (gridSize * gridSize));
    if (y < 0 || y >= gridSize) continue;
    minVX = Math.min(minVX, x);
    minVY = Math.min(minVY, y);
    minVZ = Math.min(minVZ, z);
    maxVX = Math.max(maxVX, x);
    maxVY = Math.max(maxVY, y);
    maxVZ = Math.max(maxVZ, z);
  }
  if (maxVX < 0) return null;
  return {
    minX: minVX / gridSize,
    minY: minVY / gridSize,
    minZ: minVZ / gridSize,
    maxX: (maxVX + 1) / gridSize,
    maxY: (maxVY + 1) / gridSize,
    maxZ: (maxVZ + 1) / gridSize,
  };
}

/**
 * Rotate/mirror a cell-local hitbox around the cell center using a row-major 3×3
 * orientation matrix (same convention as blockTransformMatrix).
 */
export function transformCellHitbox(
  hitbox: CellHitbox,
  matrix: ReadonlyArray<number>,
): CellHitbox {
  if (matrix.length < 9) return hitbox;
  const corners: Array<[number, number, number]> = [
    [hitbox.minX, hitbox.minY, hitbox.minZ],
    [hitbox.minX, hitbox.minY, hitbox.maxZ],
    [hitbox.minX, hitbox.maxY, hitbox.minZ],
    [hitbox.minX, hitbox.maxY, hitbox.maxZ],
    [hitbox.maxX, hitbox.minY, hitbox.minZ],
    [hitbox.maxX, hitbox.minY, hitbox.maxZ],
    [hitbox.maxX, hitbox.maxY, hitbox.minZ],
    [hitbox.maxX, hitbox.maxY, hitbox.maxZ],
  ];
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (const [x, y, z] of corners) {
    const px = x - 0.5;
    const py = y - 0.5;
    const pz = z - 0.5;
    const rx = matrix[0]! * px + matrix[1]! * py + matrix[2]! * pz + 0.5;
    const ry = matrix[3]! * px + matrix[4]! * py + matrix[5]! * pz + 0.5;
    const rz = matrix[6]! * px + matrix[7]! * py + matrix[8]! * pz + 0.5;
    minX = Math.min(minX, rx);
    minY = Math.min(minY, ry);
    minZ = Math.min(minZ, rz);
    maxX = Math.max(maxX, rx);
    maxY = Math.max(maxY, ry);
    maxZ = Math.max(maxZ, rz);
  }
  return { minX, minY, minZ, maxX, maxY, maxZ };
}

export function worldHitboxFromCell(
  cellX: number,
  cellY: number,
  cellZ: number,
  local: CellHitbox,
): CellHitbox {
  return {
    minX: cellX + local.minX,
    minY: cellY + local.minY,
    minZ: cellZ + local.minZ,
    maxX: cellX + local.maxX,
    maxY: cellY + local.maxY,
    maxZ: cellZ + local.maxZ,
  };
}

export function aabbOverlap(
  a: CellHitbox,
  b: CellHitbox,
  eps = 1e-5,
): boolean {
  return (
    a.minX < b.maxX - eps
    && a.maxX > b.minX + eps
    && a.minY < b.maxY - eps
    && a.maxY > b.minY + eps
    && a.minZ < b.maxZ - eps
    && a.maxZ > b.minZ + eps
  );
}
